import { useCallback } from "react";

import { useFastshiftAnalyticsData, useFireAnalyticsEvents } from "@/analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey
} from "@/analytics/constants";

const TABLE_EVENT_TYPE = {
    SEARCH: "search",
    PAGE_SIZE: "pageSize",
    SORT: "sort",
    NAVIGATION: "navigation"
} as const;

type TableEventType = (typeof TABLE_EVENT_TYPE)[keyof typeof TABLE_EVENT_TYPE];

const getActionSubjectIdBySource = (
    source: SourceKey,
    eventType: TableEventType
): ActionSubjectIdKey | undefined => {
    const mapping: Partial<
        Record<SourceKey, Partial<Record<TableEventType, ActionSubjectIdKey>>>
    > = {
        [SourceKey.FASTSHIFT_PORTAL_SELECTION_SCREEN]: {
            [TABLE_EVENT_TYPE.SEARCH]:
                ActionSubjectIdKey.DOMAIN_SELECTION_TABLE_SEARCH,
            [TABLE_EVENT_TYPE.PAGE_SIZE]:
                ActionSubjectIdKey.SELECTION_TABLE_CHANGE_PAGE_SIZE_DROPDOWN_ITEM,
            [TABLE_EVENT_TYPE.SORT]:
                ActionSubjectIdKey.SELECTION_TABLE_COLUMN_SORT,
            [TABLE_EVENT_TYPE.NAVIGATION]:
                ActionSubjectIdKey.SELECTION_TABLE_PAGE_NAVIGATION_BUTTON
        },
        [SourceKey.FASTSHIFT_PORTAL_MIGRATION_TICKETS_SCREEN]: {
            [TABLE_EVENT_TYPE.SEARCH]:
                ActionSubjectIdKey.MIGRATION_TICKETS_TABLE_SEARCH,
            [TABLE_EVENT_TYPE.PAGE_SIZE]:
                ActionSubjectIdKey.MIGRATION_TICKETS_TABLE_CHANGE_PAGE_SIZE_DROPDOWN_ITEM,
            [TABLE_EVENT_TYPE.NAVIGATION]:
                ActionSubjectIdKey.MIGRATION_TICKETS_TABLE_PAGE_NAVIGATION_BUTTON
        }
    };

    return mapping[source]?.[eventType];
};

export const useFastshiftTableAnalytics = () => {
    const { sourceKey, identityAttrs } = useFastshiftAnalyticsData(true);
    const { fireUIEvent } = useFireAnalyticsEvents(identityAttrs);

    const sendSearchUIEvent = useCallback(() => {
        const actionSubjectId = getActionSubjectIdBySource(
            sourceKey,
            TABLE_EVENT_TYPE.SEARCH
        );
        if (!actionSubjectId) return;
        fireUIEvent({
            action: ActionKey.SUBMITTED,
            actionSubject: ActionSubjectKey.SEARCH,
            actionSubjectId,
            source: sourceKey
        });
    }, [fireUIEvent, sourceKey]);

    const sendNavigationUIEvent = useCallback(
        (buttonType: string, totalPages: number) => {
            const actionSubjectId = getActionSubjectIdBySource(
                sourceKey,
                TABLE_EVENT_TYPE.NAVIGATION
            );
            if (!actionSubjectId) return;
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.BUTTON,
                actionSubjectId,
                source: sourceKey,
                attributes: {
                    buttonType,
                    totalPages
                }
            });
        },
        [fireUIEvent, sourceKey]
    );

    const sendPageSizeChangeUIEvent = useCallback(
        (newPageSize: number) => {
            const actionSubjectId = getActionSubjectIdBySource(
                sourceKey,
                TABLE_EVENT_TYPE.PAGE_SIZE
            );
            if (!actionSubjectId) return;
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
                actionSubjectId,
                source: sourceKey,
                attributes: {
                    newPageSize
                }
            });
        },
        [fireUIEvent, sourceKey]
    );

    const sendSortUIEvent = useCallback(
        (column: string, direction: string) => {
            const actionSubjectId = getActionSubjectIdBySource(
                sourceKey,
                TABLE_EVENT_TYPE.SORT
            );
            if (!actionSubjectId) return;
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.TABLE_COLUMN_SORT,
                actionSubjectId,
                source: sourceKey,
                attributes: {
                    columnTitle: column,
                    sortType: direction
                }
            });
        },
        [fireUIEvent, sourceKey]
    );

    return {
        sendSearchUIEvent,
        sendNavigationUIEvent,
        sendPageSizeChangeUIEvent,
        sendSortUIEvent
    };
};
